import React, { useState } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, MessageSquare, Loader2, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScheduleModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { currentLanguage } = useLanguage();

  const content = {
    pt: {
      title: 'Agendar Reunião',
      subtitle: 'Escolha a data e hora que melhor lhe convém',
      name: 'Nome',
      email: 'Email',
      phone: 'Telefone',
      date: 'Data pretendida',
      time: 'Hora pretendida',
      message: 'Mensagem (opcional)',
      messagePlaceholder: 'Conte-nos um pouco sobre o vosso casamento...',
      submit: 'Confirmar Agendamento',
      success: 'Pedido Enviado!',
      successMsg: 'Entraremos em contacto para confirmar a sua reunião.',
      close: 'Fechar',
      times: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00']
    },
    en: {
      title: 'Schedule Meeting',
      subtitle: 'Choose the date and time that suits you best',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      date: 'Preferred date',
      time: 'Preferred time',
      message: 'Message (optional)',
      messagePlaceholder: 'Tell us a bit about your wedding...',
      submit: 'Confirm Booking',
      success: 'Request Sent!',
      successMsg: 'We will contact you to confirm your meeting.',
      close: 'Close',
      times: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00']
    },
    fr: {
      title: 'Planifier une Réunion',
      subtitle: 'Choisissez la date et l\'heure qui vous conviennent',
      name: 'Nom',
      email: 'Email',
      phone: 'Téléphone',
      date: 'Date souhaitée',
      time: 'Heure souhaitée',
      message: 'Message (optionnel)',
      messagePlaceholder: 'Parlez-nous un peu de votre mariage...',
      submit: 'Confirmer le Rendez-vous',
      success: 'Demande Envoyée!',
      successMsg: 'Nous vous contacterons pour confirmer votre réunion.',
      close: 'Fermer',
      times: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00']
    },
    es: {
      title: 'Agendar Reunião',
      subtitle: 'Elige la fecha y hora que mejor te convenga',
      name: 'Nombre',
      email: 'Email',
      phone: 'Teléfono',
      date: 'Fecha deseada',
      time: 'Hora deseada',
      message: 'Mensaje (opcional)',
      messagePlaceholder: 'Cuéntanos un poco sobre vuestra boda...',
      submit: 'Confirmar Cita',
      success: '¡Solicitud Enviada!',
      successMsg: 'Nos pondremos en contacto para confirmar tu reunión.',
      close: 'Cerrar',
      times: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00', '18:00']
    }
  };

  const c = content[currentLanguage] || content.pt;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/schedule-appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          date: formData.date,
          time: formData.time,
          message: formData.message || null
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao enviar');
      }

      setSuccess(true);
      
      // Reset and close after delay
      setTimeout(() => {
        setSuccess(false);
        setFormData({ name: '', email: '', phone: '', date: '', time: '', message: '' });
        onClose();
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-fade-in max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {success ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 rounded-full bg-[#B5C4B1]/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#B5C4B1]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-3">
              {c.success}
            </h3>
            <p className="text-[#6B4E37]">{c.successMsg}</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="relative bg-gradient-to-r from-[#D4C4B0]/40 to-[#B5C4B1]/30 p-6">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-[#8B7D6B] hover:text-[#6B4E37] transition-colors"
                aria-label={c.close}
              >
                <X size={20} />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/80 flex items-center justify-center shadow-sm">
                  <Calendar size={28} className="text-[#6B4E37]" />
                </div>
                <div>
                  <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C]">
                    {c.title}
                  </h2>
                  <p className="text-sm text-[#6B4E37]">
                    {c.subtitle}
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name & Email */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#6B4E37] mb-1 flex items-center gap-2">
                    <User size={14} />
                    {c.name} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-[#2C2C2C]"
                    data-testid="schedule-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#6B4E37] mb-1 flex items-center gap-2">
                    <Mail size={14} />
                    {c.email} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-[#2C2C2C]"
                    data-testid="schedule-email"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-[#6B4E37] mb-1 flex items-center gap-2">
                  <Phone size={14} />
                  {c.phone} *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-[#2C2C2C]"
                  data-testid="schedule-phone"
                />
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#6B4E37] mb-1 flex items-center gap-2">
                    <Calendar size={14} />
                    {c.date} *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    min={getMinDate()}
                    required
                    className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-[#2C2C2C]"
                    data-testid="schedule-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#6B4E37] mb-1 flex items-center gap-2">
                    <Clock size={14} />
                    {c.time} *
                  </label>
                  <select
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-[#2C2C2C] cursor-pointer"
                    data-testid="schedule-time"
                  >
                    <option value="">--:--</option>
                    {c.times.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-[#6B4E37] mb-1 flex items-center gap-2">
                  <MessageSquare size={14} />
                  {c.message}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-[#2C2C2C] resize-none"
                  placeholder={c.messagePlaceholder}
                  data-testid="schedule-message"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#C4B5A0] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-colors disabled:opacity-50"
                data-testid="schedule-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Calendar size={18} />
                    {c.submit}
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ScheduleModal;
